/**
 * The sutherland hodgman algorithm for convex polygon clipping
 * @module
 */
import type { Face } from "./brep";
import { faceNew } from "./brep";
import type { PlaneBSP } from "./bspTypes";
import { convexHullOnPlane } from "./convexHullOnPlane";
import { DIST_EPSILON_SQUARED } from "./core";
import type { Mesh3, QuadMesh3 } from "./mesh";
import type { Plane } from "./plane";
import { planeProjectIntoTargetPlane } from "./plane";
import { planeBSPNew } from "./planeBSP";
import { polyLine2New } from "./polyLine2Types";
import type { Vec2 } from "./vec2";
import type { Vec3 } from "./vec3";
import { vec3DistSquared, vec3Dot } from "./vec3";

function signedDistanceFromPlane([x, y, z]: Vec3, { nX, nY, nZ, dist }: PlaneBSP): number {
  return vec3Dot(nX, nY, nZ, x, y, z) - dist;
}

function triangulate(polygons: number[][], outIndices: number[], outVertices: number[]) {
  // triangulate n-sided convex polygons (fan topology with no shared vertices)
  for (const vertices of polygons) {
    const x0 = vertices[0];
    const y0 = vertices[1];
    const z0 = vertices[2];

    let x1 = vertices[3];
    let y1 = vertices[4];
    let z1 = vertices[5];

    for (let i = 6; i < vertices.length; i += 3) {
      const x2 = vertices[i];
      const y2 = vertices[i + 1];
      const z2 = vertices[i + 2];

      const n = outVertices.length / 3; // number of vertices output so far
      outVertices.push(x0, y0, z0, x1, y1, z1, x2, y2, z2);
      outIndices.push(n, n + 1, n + 2);

      x1 = x2;
      y1 = y2;
      z1 = z2;
    }
  }
}

function toTrisAndQuads(polygons: number[][], outTris: number[], outQuads: number[], outVertices: number[]) {
  // triangulate n-sided convex polygons (fan topology with no shared vertices)
  for (const vertices of polygons) {
    const x0 = vertices[0];
    const y0 = vertices[1];
    const z0 = vertices[2];

    let x1 = vertices[3];
    let y1 = vertices[4];
    let z1 = vertices[5];

    let i = 6;

    if (vertices.length >= 12) {
      // First create a quad if the polygon has >= 4 vertices.
      const x2 = vertices[i];
      const y2 = vertices[i + 1];
      const z2 = vertices[i + 2];
      i += 3;
      const x3 = vertices[i];
      const y3 = vertices[i + 1];
      const z3 = vertices[i + 2];
      i += 3;
      const n = outVertices.length / 3; // number of vertices output so far
      outVertices.push(x0, y0, z0, x1, y1, z1, x2, y2, z2, x3, y3, z3);
      outQuads.push(n, n + 1, n + 2, n + 3);

      x1 = x3;
      y1 = y3;
      z1 = z3;
    }

    for (; i < vertices.length; i += 3) {
      const x2 = vertices[i];
      const y2 = vertices[i + 1];
      const z2 = vertices[i + 2];

      const n = outVertices.length / 3; // number of vertices output so far
      outVertices.push(x0, y0, z0, x1, y1, z1, x2, y2, z2);
      outTris.push(n, n + 1, n + 2);

      x1 = x2;
      y1 = y2;
      z1 = z2;
    }
  }
}

export function clipPolygonAgainstPlane(
  vertices: number[],
  planeEqn: PlaneBSP,
  coplanarVertices: number[] | null = null,
): number[] {
  const intersectLineWithPlane = ([x0, y0, z0]: Vec3, [x1, y1, z1]: Vec3, [d0, d1]: Vec2): Vec3 => {
    const t = d0 / (d0 - d1);
    const x = x0 + t * (x1 - x0);
    const y = y0 + t * (y1 - y0);
    const z = z0 + t * (z1 - z0);
    return [x, y, z];
  };

  const saveIntersectionVertex = ([xi, yi, zi]: Vec3) => {
    if (coplanarVertices) {
      let unique = true; // save intersection vertex if not a duplicate
      for (let i = 0; unique && i < coplanarVertices.length; i += 3) {
        const xc = coplanarVertices[i];
        const yc = coplanarVertices[i + 1];
        const zc = coplanarVertices[i + 2];
        unique = vec3DistSquared(xi, yi, zi, xc, yc, zc) > DIST_EPSILON_SQUARED;
      }
      if (unique) {
        coplanarVertices.push(xi, yi, zi);
      }
    }
  };

  const outVertices: number[] = [];
  let v0: Vec3 = [vertices[vertices.length - 3], vertices[vertices.length - 2], vertices[vertices.length - 1]];
  let d0 = signedDistanceFromPlane(v0, planeEqn);

  for (let i = 0; i < vertices.length; i += 3) {
    const v1: Vec3 = [vertices[i], vertices[i + 1], vertices[i + 2]];
    const d1 = signedDistanceFromPlane(v1, planeEqn);

    if (d0 <= 0.0) {
      if (d1 <= 0.0) {
        outVertices.push(...v1);
      } else {
        const vi = intersectLineWithPlane(v0, v1, [d0, d1]);
        saveIntersectionVertex(vi);
        outVertices.push(...vi);
      }
    } else {
      if (d1 <= 0.0) {
        const vi = intersectLineWithPlane(v0, v1, [d0, d1]);
        saveIntersectionVertex(vi);
        outVertices.push(...vi);
        outVertices.push(...v1);
      }
    }

    v0 = v1;
    d0 = d1;
  }

  return outVertices;
}

export function clipPolygonsAgainstPlane(
  polygons: number[][],
  planeEqn: PlaneBSP,
  capConvexHull: boolean = false,
): number[][] {
  const coplanarVertices: number[] | null = capConvexHull ? [] : null;
  const outputPolygons: number[][] = [];

  for (let vertices of polygons) {
    vertices = clipPolygonAgainstPlane(vertices, planeEqn, coplanarVertices);
    if (vertices.length >= 9) {
      outputPolygons.push(vertices);
    }
  }

  if (capConvexHull) {
    const vertices = convexHullOnPlane(planeEqn, coplanarVertices);
    if (vertices.length >= 9) {
      outputPolygons.push(vertices);
    }
  }

  return outputPolygons;
}

export function clipFaceAgainstPlanes(face: Face, planes: Plane[]): Face | null {
  let polygons = face.loops.map((loop) => {
    const polygon: number[] = []; // a polygon is an open loop in the face
    for (let i = 0, len = loop.length - 2; i < len; i += 2) {
      polygon.push(loop[i], loop[i + 1], 0.0);
    }
    return polygon;
  });

  for (const plane of planes) {
    const planeEqn = planeBSPNew(...planeProjectIntoTargetPlane(plane, face.plane));
    polygons = clipPolygonsAgainstPlane(polygons, planeEqn);
    if (polygons.length === 0) {
      break; // face completely clipped away
    }
  }

  if (polygons.length > 0) {
    const clippedLoops = polygons.map((polygon) => {
      const pl = polyLine2New((polygon.length / 3) * 2 + 2);
      for (let i = 0, j = 0, len = polygon.length; i < len; i += 3) {
        pl[j++] = polygon[i];
        pl[j++] = polygon[i + 1];
      }
      pl[pl.length - 2] = polygon[0];
      pl[pl.length - 1] = polygon[1]; // close the loop
      return pl;
    });
    return faceNew(clippedLoops, face.plane);
  }

  return null;
}

export function clipFacesAgainstPlanes(faces: Face[], planes: Plane[]): Face[] {
  return faces.map((f) => clipFaceAgainstPlanes(f, planes)).filter((f) => f !== null) as Face[];
}

function convertTrisToPolygons(verts: Float32Array, tris: Uint32Array, polygonsOut: number[][]) {
  for (let i = 0; i < tris.length; i += 3) {
    const i0 = 3 * tris[i]; // vertex 0 index
    const i1 = 3 * tris[i + 1]; // vertex 1 index
    const i2 = 3 * tris[i + 2]; // vertex 2 index
    polygonsOut.push([
      verts[i0], // x0
      verts[i0 + 1], // y0
      verts[i0 + 2], // z0
      verts[i1], // x1
      verts[i1 + 1], // y1
      verts[i1 + 2], // z1
      verts[i2], // x2
      verts[i2 + 1], // y2
      verts[i2 + 2], // z2
    ]);
  }
}

function convertQuadsToPolygons(verts: Float32Array, quads: Uint32Array, polygonsOut: number[][]) {
  for (let i = 0; i < quads.length; i += 4) {
    const i0 = 3 * quads[i]; // vertex 0 index
    const i1 = 3 * quads[i + 1]; // vertex 1 index
    const i2 = 3 * quads[i + 2]; // vertex 2 index
    const i3 = 3 * quads[i + 3]; // vertex 3 index
    polygonsOut.push([
      verts[i0], // x0
      verts[i0 + 1], // y0
      verts[i0 + 2], // z0
      verts[i1], // x1
      verts[i1 + 1], // y1
      verts[i1 + 2], // z1
      verts[i2], // x2
      verts[i2 + 1], // y2
      verts[i2 + 2], // z2
      verts[i3], // x3
      verts[i3 + 1], // y3
      verts[i3 + 2], // z3
    ]);
  }
}

export function clipQuadMesh3AgainstPlanes(
  { tris, quads, verts }: QuadMesh3,
  planeEqns: PlaneBSP[],
  capConvexHull: boolean,
): QuadMesh3 {
  const outVertices: number[] = [];
  const outTris: number[] = [];
  const outQuads: number[] = [];
  let polygons: number[][] = [];

  convertTrisToPolygons(verts, tris, polygons);
  convertQuadsToPolygons(verts, quads, polygons);

  for (const eqn of planeEqns) {
    polygons = clipPolygonsAgainstPlane(polygons, eqn, capConvexHull);
    if (polygons.length === 0) {
      break;
    }
  }

  toTrisAndQuads(polygons, outTris, outQuads, outVertices);

  return {
    verts: new Float32Array(outVertices),
    tris: new Uint32Array(outTris),
    quads: new Uint32Array(outQuads),
  };
}

export function clipMesh3AgainstPlanes({ tris, verts }: Mesh3, planeEqns: PlaneBSP[], capConvexHull: boolean): Mesh3 {
  const outVertices: number[] = [];
  const outIndices: number[] = [];
  let polygons: number[][] = [];

  convertTrisToPolygons(verts, tris, polygons);

  for (const eqn of planeEqns) {
    polygons = clipPolygonsAgainstPlane(polygons, eqn, capConvexHull);
    if (polygons.length === 0) {
      break;
    }
  }

  triangulate(polygons, outIndices, outVertices);

  return {
    tris: new Uint32Array(outIndices),
    verts: new Float32Array(outVertices),
  };
}
